import React, { useEffect, useMemo, useRef, useState } from "react";
import { colors, icons, BlockLabel, ActionMenu, DropdownSingle, NumberInput, TextInput } from "oolib";
import { getBlockLabelProps } from "oolib/dist/utils/getBlockLabelProps";
//local utils
import { useTheme } from "styled-components";
import { getPrimaryColor100, useLocale } from "oolib/dist/utilsOolib";
import { AddColButton, AddRowButton } from "./comps/AddRowColButtons";

import { UIContent } from "@/UIContent";
import { ColActionsMenu } from "./comps/rowColActionMenus/ColActionsMenu";
import { RowActionsMenu } from "./comps/rowColActionMenus/RowActionsMenu";
import { handleAddCol } from "./handlers/handleColActions";
import { handleAddRow } from "./handlers/handleRowActions";
import { handleStretchToFullWidth } from "./handlers/handleStretchToFullWidth";
import { handleToggleRowHeader } from "./handlers/handleToggleRowHeader";
import { handleToggleColHeader } from "./handlers/handleToggleColHeader";
import {
  StyledSimpleTable,
  StyledSimpleTableCell,
  StyledSimpleTableHeader,
  StyledSimpleTableHeaderCell,
  StyledSimpleTableRow,
} from "./styled";
import { useResizeTableColumns } from "./useResizeTableColumns";
import { convertColHeaderConfigToRowData } from "./utils/convertColHeaderConfigToRowData";
import { convertRowHeaderConfigToCellData } from './utils/convertRowHeaderConfigToCellData';
import { prepInitValueFromConfigIfNoValue } from "./utils/prepInitValueFromConfigIfNoValue";

import { UI_BODY_BOLD_SM } from "oolib/dist/v2/components/Typo2";

const { ArrowDown } = icons;
const { greyColor100 } = colors;

/**
 * PENDINGS
 * - Ideally should migrate RichTextEditor to Lexical
 * - Should run a function that converts rich text to plain text and sends that out in onChange. (like how we do for TitleInput)
 * - for backwards compatibility, in the initValueSetting function, we should convert all plain text values to lexical shape & all draftjs shaped values also to lexical shape
 * - memoization of cell comps maybe? there is some commented code for the same below..
 * - KNOWN ISSUE: when the number input block is used, the component is lagging like mad. might have something to
 *   do with the memoization issue
 */

export function SimpleTable({
  id,
  readOnly,
  config = {
    noOfCols: 3,
    noOfRows: 3,
    headerForRowHeadersColumn: undefined,
    colHeaderData: undefined,
    rowHeaderData: undefined,
    disableAddRow: false,
    disableRowActionsMenu: false,
    disablePrimaryActionsMenu: false,
    blockConfig: [undefined, { comp: 'DropdownSingle', props: {} }, { comp: 'DropdownSingle', props: {} }]
  },
  value: _value,
  onChange,
  defaultColWidth = 200,

  /**
   * config: {
   *  noOfCols: 2
   *  noOfRows: 3
   *  colHeaderData: ['col1', 'col2', 'col3']
   *  // if colHeaderData is explicitly defined in config, then
   *       - first row is styled as header, (value.data[0].isColHeader: true)
   *       - is uneditable (value.data[0].cellData.<each>.readOnly: true)
   *       - user cannot toggle header on/off (canToggleColHeaderStyle: false)
   *       - enableColActions: undefined
   *       - noOfCols = colHeaderData.length , so config.noOfCols doesnt have to be defined
   *  rowHeaderData: ['row1', 'row2', 'row3']
   *  // if rowHeaderData is explicitly defined in config, then
   *     - first col is styled as header, (firstColHeader: true)
   *     - is uneditable (disableColHeaderEdit: true)
   *     - user cannot toggle header on/off (toggleColHeaderOnOff: false)
   *     - enableRowActions: undefined
   *     - noOfRows = rowHeaderData.length , so config.noOfRows doesnt have to be defined
   *  blockConfig: [ {comp, props}|undefined, {comp, props}|undefined, ...]
   *  // based on the number of cols extracted from either colHeaderData or noOfCols, if blockConfig
   *     is defined, it is assigned to the equivalent idx of column
   *  }
   *
   *  VALUE RESHAPE IS DONE
   *  value: {
   *    data: [ { cellData: [ {...}, {...} ] }, ... ],
   *    colWidthConfig: { [colId]: Number }
   *  }
   */
}) {
  
  const props = arguments[0];
  const localize = useLocale();
  const theme = useTheme();

  const { RichTextEditor /*: _RichTextEditor*/, convertToRichText } =
    theme || {};

  //memoizing cuz we dont want all cell rtes to render everytime one cell changes
  // const RichTextEditor = useMemo(() => {
  //   const checkMemoFn = (prevProps, currentProps) => {
  //     const {
  //       value: prevVal,
  //     } = prevProps
  //     const {
  //       value: currentVal
  //     } = currentProps

  //     return isEqual(prevVal, currentVal)
  //   }
  //   return _RichTextEditor && React.memo(_RichTextEditor, checkMemoFn)
  // },[])

  const {
    value,
    enableColActions, //returns all possible actions config if no config.colHeaderData is defined. else undefined\
    enableRowActions,
  } = useMemo(
    () =>
      prepInitValueFromConfigIfNoValue({
        //memo v.imp cuz otherwise cell and row id keep changing on rerender if value doesnt exist, and this messes with their react keys, this leads to bugs
        _value,
        config,
        defaultColWidth,
      }),
    [_value]
  );

  const {
    resetColResizeState,
    hideShowDragZoneIndicator,
    initColResizeState,
    resetDragZoneIndicator,
    handleResizeColumn,
    colResizeState,
    dragZoneWidth,
  } = useResizeTableColumns({
    setColWidthConfig: (setterFn) => {
      const newColWidthConfig = setterFn(value.colWidthConfig);
      onChange(id, { ...value, colWidthConfig: newColWidthConfig });
    }, // here we are replicating the setState(prev => do something) behaviour
    colWidthConfig: value.colWidthConfig,
    theme,
  });

  useEffect(() => {
    window.addEventListener("mouseup", resetColResizeState);
    window.addEventListener("mousemove", handleResizeColumn);
    return () => {
      window.removeEventListener("mousemove", handleResizeColumn);
      window.removeEventListener("mouseup", resetColResizeState);
    };
  }, [colResizeState]);

  const CellContentBlocks = {
    RichTextEditor,
    DropdownSingle,
    NumberInput,
    TextInput,
  };

  const CellContentBlockDefaultProps = {
    RichTextEditor: {
      variant: "simple",
      typo: "SANS_3",
      placeholder: " ",
      style: {
        padding: "0.9rem 1rem",
      },
    },
    DropdownSingle: {
      selectStyleOverride: {
        backgroundColor: colors.none,
      },
      popOutOfOverflowHiddenParent: true,
    },
    NumberInput: {
      // style: {
      //   height: '100%',
      // },
    },
    TextInput: {
      // style: {
      //   height: '100%',
      // },
    },
  };

  const CellValueGetters = {
    RichTextEditor: (v) => (convertToRichText ? convertToRichText(v) : v),
  }; //prob dont need this..

  const [actionsActiveOnRow, setActionsActiveOnRow] = useState(undefined);
  const [actionsActiveOnCol, setActionsActiveOnCol] = useState(undefined);

  const [hoveredRowAndCol, setHoveredRowAndCol] = useState({
    rowIdx: undefined,
    colIdx: undefined,
  });

  

  const handleCellInputChange = ({ cellIdx, rowIdx, v }) => {
    const valData = value.data;
    const { colWidthConfig } = value;
    let newData = [...valData];
    newData[rowIdx].cellData[cellIdx].value = v;
    onChange && onChange(id, { data: newData, colWidthConfig });
  };

  /**
   * we are explicitly defining this here and passing it on to AddColButton comp
   * since we need this width to calculate stretch to full width
   */
  const addColButtonWidth = 20;

  // these are explicitly defined here cuz we need to calc offset padding and margin using these, so that overflowX doesnt hide them
  const rowActionsButtonWidth = 15;
  const colActionsButtonHeight = 15;
  const primaryTableActionsMenuHeight = 30;
  const primaryTableActionsMenuGap = 5;

  const genCell = ({
    cell, 
    cellIdx, 
    rowIdx, 
    enableColResizeActions = true // cuz in the case of pre fixed column (via config.rowHeaderData), enableResize will be set to false
  }) => {
    const cellCompName = cell.comp || 'RichTextEditor';
    const NoCompPlaceholder = () => <div>No comp exists</div>;
    
    let CellComp = CellContentBlocks[cellCompName] || NoCompPlaceholder
    
    let cellProps = CellContentBlockDefaultProps[cellCompName] ? {...CellContentBlockDefaultProps[cellCompName], ...(cell.props || {}) } : {};
    let cellValue = CellValueGetters[cellCompName] ? CellValueGetters[cellCompName](cell.value) : cell.value;
    let cellWidth = value.colWidthConfig[`col_${cellIdx}`] || defaultColWidth+'px'; //cuz when pre-fixed column is applied via config.rowHeaderData, no colWidthConfig will be there for it in value.colWidthConfig

    //(100% - totalExplicitWidths)/(totalColumns - noOfColsWithExplicitWidths)
    // let cellProps = {
    // 	...CellContentBlockDefaultProps[cell.comp],
    // 	...(cell.props ? cell.props : {}),
    // }
    if(cell.isRowHeader) console.log({cell})
    const StyledCellWrapper = cell.isRowHeader
        ? StyledSimpleTableHeaderCell
        : StyledSimpleTableCell;

  return (
    <StyledCellWrapper
      key={cell.id}
      id={`kp_table__cell__${cell.id}`}
      readOnly={readOnly || cell.readOnly}
      style={{
        color: greyColor100,
        width: `${cellWidth}`,
        borderRight:
          colResizeState?.colId === `col_${cellIdx}` &&
          `2px solid ${getPrimaryColor100(theme?.colors)}`,
      }}
      actionsActiveOnRow={actionsActiveOnRow}
      colIdx={cellIdx}
      rowIdx={rowIdx}
      totRows={value.data.length}
      actionsActiveOnCol={actionsActiveOnCol}
      {...(
        enableColResizeActions
        ? {
          onMouseEnter: (e) => {
            setHoveredRowAndCol({
              rowIdx: rowIdx,
              colIdx: cellIdx,
            });
          },
          onMouseDown: (e) => {
            initColResizeState(e, { colId: `col_${cellIdx}` });
          },
          onMouseOut: e => resetDragZoneIndicator({
            e, 
            ...(cell.isRowHeader ? { borderStyleInactive : `2px solid ${colors.greyColor10}`} : {})
          }),
          onMouseMove: e => hideShowDragZoneIndicator({
            e,
            ...(cell.isRowHeader ? { 
              borderStyleInactive : `2px solid ${colors.greyColor10}`,
              borderStyleActive : `2px solid ${getPrimaryColor100(theme?.colors)}`
            } : {})
          })
        } : {}
      )}
      tabIndex={0} //note: has to be camelcase to work
    >
      {
        <ColActionsMenu
          {...{
            value,
            colIdx: cellIdx,
            onChange,
            id,
            defaultColWidth,
            setActionsActiveOnCol,
            colActionsButtonHeight,
            conditionsToRender:
              !readOnly &&
              enableColActions &&
              rowIdx === 0 &&
              hoveredRowAndCol.colIdx === cellIdx,
          }}
        />
      }

      {
        !config.disableRowActionsMenu && <RowActionsMenu
          {...{
            value,
            rowIdx,
            onChange,
            id,
            setActionsActiveOnRow,
            rowActionsButtonWidth,
            disableAddRow: config?.disableAddRow,
            blockConfig: config?.blockConfig,
            conditionsToRender:
              !readOnly &&
              enableRowActions &&
              cellIdx === 0 &&
              rowIdx !== undefined && //cuz for fixed col headers, rowIdx is undefined
              hoveredRowAndCol.rowIdx === rowIdx,
          }}
        />
      }
      <div style={{ marginRight: dragZoneWidth + "px" }}>
        <CellComp
          {...cellProps}
          id={
            `kp_table_cell__${cell.id}__rich_input` /*dont mess with this id nomenclature. it needs to stay this way. check out the handleCellInputChange fn to understand */
          }
          value={cellValue}
          readOnly={
            readOnly || cell.readOnly
            /* if the table itself is readonly, then make every cell readonly, no questions asked. if not, then depend on what the readonly status of each individual cell is */
          }
          onChange={(k, v) =>
            handleCellInputChange({ v, rowIdx, cellIdx })
          }
        />
      </div>
    </StyledCellWrapper>
  );
  }


  const genRow = ({ row, rowIdx }) => {
    const StyledWrapper = row.isColHeader
      ? StyledSimpleTableHeader
      : StyledSimpleTableRow;
    
    // const NoCompPlaceholder = () => <div>No comp exists</div>;
    // let CellCompNameForHeaderForRowHeadersColumn = CellContentBlocks["RichTextEditor"] || NoCompPlaceholder

    return (
      <StyledWrapper
        actionsActiveOnRow={actionsActiveOnRow}
        // onClick={() => row.link && history.push(row.link)}
        style={{ display: "flex" }}
        readOnly={readOnly}
        rowIdx={rowIdx}
        key={row.id}
      >
        {/* Handle both corner header cell and regular row header cell */}
        {config.rowHeaderData && (
          row.isColHeader && config.headerForRowHeadersColumn !== undefined ? (
            <StyledSimpleTableHeaderCell
              key="cornerHeaderCell"
              id="kp_table__cell__cornerHeaderCell"
              readOnly={true}
              style={{
                color: greyColor100,
                width: value.colWidthConfig[`col_corner`] || defaultColWidth+'px',
              }}
            >
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", padding: "0.9rem 1rem" }}>
                {/* <CellCompNameForHeaderForRowHeadersColumn
                  id="kp_table_cell__cornerHeaderCell__rich_input"
                  value={config.headerForRowHeadersColumn}
                  readOnly={true}
                /> */}
                <UI_BODY_BOLD_SM>{config.headerForRowHeadersColumn}</UI_BODY_BOLD_SM>
                <ArrowDown size={14} weight="bold"/>
              </div>
            </StyledSimpleTableHeaderCell>
          ) : (
            genCell({
              cell: convertRowHeaderConfigToCellData({
                rowHeaderData: config.rowHeaderData.map(d => localize(d)),
                convertToRichText,
                rowIdx
              }),
              enableColResizeActions: false
            })
          )
        )}
        {row.cellData.map((cell, cellIdx) => genCell({cell, cellIdx, rowIdx}))}
      </StyledWrapper>
    );
  };
  const fullWidthCompWrapper = useRef(null);

  return (
    <div>
      <BlockLabel {...getBlockLabelProps(props)} />
      <div
        style={{
          overflowX: "auto",
          paddingLeft: rowActionsButtonWidth / 2 + "px",
          marginLeft: "-" + rowActionsButtonWidth / 2 + "px",
          paddingTop:
            primaryTableActionsMenuGap + primaryTableActionsMenuHeight + "px",
        }}
      >
        <div
          ref={fullWidthCompWrapper}
          style={{
            display: "flex",
            alignItems:
              "stretch" /**without stretch AddColButton will be wonky */,
          }}
        >
          <div style={{ position: "relative" }}>
            {!readOnly && !config.disablePrimaryActionsMenu && (
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: "-" + primaryTableActionsMenuGap + "px",
                  transform: "translateY(-100%)",
                  zIndex: 10,
                }}
              >
                <ActionMenu
                  ButtonComp={"ButtonPrimary"}
                  actions={[
                    {
                      display: UIContent.SimpleTable.stretchToFullWidth,
                      onClick: () =>
                        handleStretchToFullWidth({
                          value,
                          onChange,
                          id,
                          enableColActions,
                          addColButtonWidth,
                          fullWidthCompWrapper,
                        }),
                    },
                    ...(enableRowActions
                      ? [
                          {
                            icon: "SquareHalf",
                            display: value.data[0].cellData[0].isRowHeader
                              ? UIContent.SimpleTable.removeHeaderCol
                              : UIContent.SimpleTable.addHeaderCol,
                            onClick: () => handleToggleColHeader({ value, onChange, id }),
                          },
                        ]
                      : []),
                    ...(enableColActions
                      ? [
                          {
                            icon: "SquareHalfBottom",
                            display: value.data[0].isColHeader
                              ? UIContent.SimpleTable.removeHeaderRow
                              : UIContent.SimpleTable.addHeaderRow,
                            onClick: () =>
                              handleToggleRowHeader({ value, onChange, id }),
                          },
                        ]
                      : []),
                  ]}
                />
              </div>
            )}
            <StyledSimpleTable
              onMouseLeave={() =>
                setHoveredRowAndCol({ rowIdx: undefined, colIdx: undefined })
              }
            >
              {config.colHeaderData &&
                genRow({
                  row: convertColHeaderConfigToRowData({
                    colHeaderData: config.colHeaderData.map(d => localize(d)),
                    convertToRichText,
                  }),
                })}
              {value.data.map((row, rowIdx) => genRow({ row, rowIdx }))}
            </StyledSimpleTable>
            {!readOnly && enableRowActions && !config.disableAddRow && (
              <AddRowButton onClick={() => handleAddRow(value, props)} />
            )}
          </div>
          {!readOnly && enableColActions && (
            <AddColButton
              enableRowActions={enableRowActions}
              addColButtonWidth={addColButtonWidth}
              onClick={() =>
                handleAddCol({ value, onChange, id, defaultColWidth })
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}
